import toast from 'react-hot-toast';

// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://stag-api.quicklook.in/api';

// Token decode function (matching AuthContext encoding)
const decodeToken = (encodedToken) => {
  try {
    return atob(atob(encodedToken)); // Double decode
  } catch (error) {
    console.error('Token decode error:', error);
    return null;
  }
};

// API Endpoints
export const API_ENDPOINTS = {
  LOGIN: '/users/login',
  LOGOUT: '/users/logout',
  REGISTER: '/users/register',
  GET_USERS: '/users/',
  GET_CUSTOMERS: '/users/our-users',
  DELETE_USER: '/users/delete/',
  UPDATE_USER: '/users/edit/',
  SCRAPE_DATA: '/scraper/scrape',
  GET_ALL_SHIPMENTS: '/awb/allshipments',
  GET_SHIPMENT: '/awb/shipment',
  BULK_APPROVE_AWB: '/awb/bulk-approve',
  CUSTOMER_REQUESTS: '/users/customer-requests',
  UPDATE_AWB: '/awb/update',
  GET_USER_SHIPMENTS: '/awb/user-customer-details',
  DOWNLOAD_AWB: '/awb/download/awb/',
  DOWNLOAD_LEO: '/awb/download/leo/',
  DOWNLOAD_INVPL: '/awb/download/invpl/',
  FORGOT_PASSWORD: '/users/forgot-password',
  VERIFY_OTP: '/users/verify-otp',
  RESET_PASSWORD: '/users/reset-password',
  CHANGE_PASSWORD: '/users/change-password',
  UPDATE_PROFILE: '/users/update-profile/',
  EXTRACT_SEA_EXPORT_PDF: '/awb/extract-sea-export',
  GET_SEA_EXPORT: '/sea-export',
  UPDATE_SEA_EXPORT: '/sea-export',
  DELETE_SEA_EXPORT: '/sea-export',
  SEA_EXPORT_ACCOUNTANT_PRESIGNED_URLS: '/sea-export/get-accountant-upload-urls',
  SEA_EXPORT_ACCOUNTANT_UPDATE: '/sea-export/accountant',
  INVOICE_ADD_CUSTOMER: '/awb/invoices',
  INVOICE_GET_CUSTOMERS: '/awb/invoices',
  INVOICE_SUBMIT: '/invoices',
  INVOICE_MAX_NUMBER: '/invoices/max-invoice-number',
  HSN_CODES: '/invoices/hsn-codes',
  S3_UPLOAD_URL: '/s3/upload-url',
  INVOICE_SUBMIT_SEA: '/invoices/sea',
  INVOICE_UPDATE: '/invoices',
  INVOICE_UPDATE_SEA: '/invoices/sea',
  INVOICE_DELETE: '/invoices',
  INVOICE_DELETE_SEA: '/invoices/sea',
  INVOICE_GET_SEA_BY_USER: '/invoices/sea/user',
  INVOICE_GET_BY_USER: '/invoices/user',
  INVOICE_DELETE_CUSTOMER: '/awb/invoices',
  NEW_BOOKING: '/new-bookings',
  NEW_BOOKING_UPLOAD_URL: '/new-bookings/upload-url',
  GET_USER_BOOKINGS: '/new-bookings/user',
  // Notifications
  NOTIFICATION_LIST: '/notifications',            // GET  — fetch all (supports ?unreadOnly=true)
  NOTIFICATION_UNREAD_COUNT: '/notifications/unread-count', // GET  — unread badge count
  NOTIFICATION_MARK_READ: '/notifications',       // PUT  — /:id/read
  NOTIFICATION_READ_ALL: '/notifications/read-all', // PUT  — mark all read
  NOTIFICATION_DELETE: '/notifications',          // DELETE — /:id
  // FCM Push Notifications
  NOTIFICATION_FCM_TOKEN: '/notifications/fcm/token',
  // Payments
  CREATE_ORDER: '/payments/create-order',
  VERIFY_PAYMENT: '/payments/verify',
  // Wallet
  WALLET_MY: '/payments/wallet/me',
  WALLET_ALL: '/payments/wallet',
  WALLET_CREATE: '/payments/wallet',
  WALLET_BY_USER: '/payments/wallet/user',
  WALLET_CREDIT: '/payments/wallet',
  WALLET_DEBIT: '/payments/wallet',
  WALLET_STATUS: '/payments/wallet',
};

// Helper function to construct full URL
export const getApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};

// Helper function to get auth headers
export const getAuthHeaders = (includeAuth = true) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  };

  if (includeAuth) {
    const encodedToken = localStorage.getItem('token');
    if (encodedToken) {
      const token = decodeToken(encodedToken);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
  }

  return headers;
};

// Universal API call function with automatic bearer token
export const apiCall = async (endpoint, options = {}) => {
  const { skipAuth = false, ...fetchOptions } = options;
  
  // Determine if this is a login endpoint
  const isLoginEndpoint = endpoint.includes('/users/login') || endpoint.includes('/login');
  
  // Prepare headers with cache control for GET requests
  const headers = {
    ...getAuthHeaders(!isLoginEndpoint && !skipAuth),
    ...options.headers
  };
  
  // Add cache control headers for GET requests to avoid stale data
  if (!fetchOptions.method || fetchOptions.method === 'GET') {
    headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    headers['Pragma'] = 'no-cache';
    headers['Expires'] = '0';
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers
    });

    // Check for concurrent login detection - use sessionStorage for synchronization
    const logoutInProgress = sessionStorage.getItem('logout_in_progress');
    
    if (!logoutInProgress) {
      try {
        const clonedResponse = response.clone();
        const responseData = await clonedResponse.json();
        
        // Check if response contains message about being logged in on another device
        const message = responseData?.message || responseData?.msg || '';
        const normalizedMessage = String(message || '').toLowerCase();
        const isConcurrentLoginMessage =
          normalizedMessage.includes('you have been logged in on another device') ||
          normalizedMessage.includes('you have been logged in on another web device');

        const isSessionExpired =
          normalizedMessage.includes('session expired') ||
          normalizedMessage.includes('please login again') ||
          normalizedMessage.includes('token expired') ||
          response.status === 401;

        if (isSessionExpired && !isConcurrentLoginMessage) {
          const alreadyShown = sessionStorage.getItem('logout_in_progress');
          if (!alreadyShown) {
            sessionStorage.setItem('logout_in_progress', 'true');
            toast.error('Session expired. Please login again.', {
              duration: 3000,
              position: 'top-center',
            });
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setTimeout(() => {
              sessionStorage.removeItem('logout_in_progress');
              const host = window.location.hostname;
              const isLocalhost = host === 'localhost' || host === '127.0.0.1';
              if (isLocalhost) {
                const port = window.location.port ? `:${window.location.port}` : '';
                window.location.href = `http://localhost${port}/?_from=quicklook`;
              } else {
                window.location.href = 'https://client.hmrspedition.com/?_from=quicklook';
              }
            }, 1000);
          }
          return response;
        }

        if (isConcurrentLoginMessage) {
          // Immediately set flag to prevent other API calls from showing message
          const alreadyShown = sessionStorage.getItem('logout_in_progress');
          if (alreadyShown) {
            // Another call already handling logout
            return response;
          }
          
          // Set flag immediately - before any other operations
          sessionStorage.setItem('logout_in_progress', 'true');
          
          // Show toast notification once
          toast.error('You have been logged in on another web device.', {
            duration: 4000,
            position: 'top-center',
          });
          
          // Store logout time if user exists
          const savedUser = localStorage.getItem('user');
          if (savedUser) {
            try {
              const user = JSON.parse(savedUser);
              if (user && user.id) {
                const logoutTime = new Date().toISOString();
                localStorage.setItem(`lastLogout_${user.id}`, logoutTime);
              }
            } catch (e) {
              console.error('Error parsing user data:', e);
            }
          }
          
          // Clear session
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          
          // Redirect to login after a short delay
          setTimeout(() => {
            sessionStorage.removeItem('logout_in_progress'); // Clear flag before redirect
            const host = window.location.hostname;
            const isLocalhost = host === 'localhost' || host === '127.0.0.1';
            if (isLocalhost) {
              const port = window.location.port ? `:${window.location.port}` : '';
              window.location.href = `http://localhost${port}/?_from=quicklook`;
            } else {
              window.location.href = 'https://client.hmrspedition.com/?_from=quicklook';
            }
          }, 1000);
          
          // Return response for any pending handlers
          return response;
        }
      } catch (parseError) {
        // If response is not JSON or already consumed, continue normally
        console.log('Response parse skipped (non-JSON or already consumed)');
      }
    }

    return response;
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};
